// client/src/context/HealthDataContext.js
/**
 * Global state för health-data: postnummer, vald vecka, symptomfilter,
 * geografisk scope, fetchade rapporter. Gör initial nationwide fetch vid
 * Dashboard-startup och håller cache mellan vyer.
 */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import API_CONFIG from '../config/apiConfig';

const HealthDataContext = createContext();

// ===== UTILITY FUNCTIONS (Generella) =====
const formatPostalCode = (postalCode) => {
  if (!postalCode) return '';
  
  const cleaned = String(postalCode).replace(/\s/g, '');
  
  if (cleaned.length === 5 && /^\d+$/.test(cleaned)) {
    return `${cleaned.substring(0, 3)} ${cleaned.substring(3)}`;
  }
  
  return cleaned;
};

const normalizePosition = (position) => {
  if (!position) return { lat: 59.3293, lng: 18.0686 };
  
  if (Array.isArray(position) && position.length === 2) {
    return { lat: position[0], lng: position[1] };
  }
  
  if (position.lat !== undefined && position.lng !== undefined) {
    return position;
  }
  
  return { lat: 59.3293, lng: 18.0686 };
};

// ===== MAIN PROVIDER - ENKEL =====
export const HealthDataProvider = ({ children }) => {
  // ===== BASIC STATE - ENKEL =====
  const [selectedWeek, setSelectedWeek] = useState(53);
  const [selectedPeriod, setSelectedPeriod] = useState('week_53_2024');
  const [selectedPostalCode, setSelectedPostalCode] = useState(null);
  
  // ===== SYMPTOM STATE - ENKEL =====
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [availableSymptoms] = useState([
    'Feber', 'Hosta', 'Andningssvårigheter', 'Trötthet', 
    'Huvudvärk', 'Muskelvärk', 'Halsont', 'Snuva', 'Frossa',
    'Illamående', 'Kräkningar', 'Diarré', 'Magont', 'Yrsel'
  ]);
  
  // ===== GEOGRAPHIC STATE - ENKEL =====
  const [geoApiParams, setGeoApiParams] = useState({
    city: null,
    postalCode: null,
    country: null
  });
  
  // ===== DATA STATE - ENKEL =====
  const [currentWeekData, setCurrentWeekData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // ===== MAP STATE - ENKEL =====
  const [mapCenter, setMapCenter] = useState({ lat: 59.3293, lng: 18.0686 });
  const [zoom, setZoom] = useState(6);
  const [visualizationType, setVisualizationType] = useState('markers');
  
  // ===== HISTORICAL DATA STATE - ENKEL =====
  const [historicalDataCache, setHistoricalDataCache] = useState(new Map());
  const [historicalLoading, setHistoricalLoading] = useState(new Map());
  const [historicalErrors, setHistoricalErrors] = useState(new Map());
  
  // ===== REQUEST TRACKING =====
  const lastRequestKeyRef = useRef('');
  const initialFetchDoneRef = useRef(false);

  // ===== FIXED: API DATA FETCHING med Railway backend + INITIAL NATIONWIDE FETCH =====
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedWeek) return;
      
      const targetPostalCode = selectedPostalCode || geoApiParams.postalCode;
      
      // Create request key to prevent duplicates
      const requestKey = `${selectedWeek}_${selectedSymptoms.join(',')}_${geoApiParams.city || ''}_${targetPostalCode || ''}`;
      
      // ✅ FIXED: Allow initial nationwide fetch for Dashboard
      const isInitialNationwideFetch = !initialFetchDoneRef.current && 
                                        !selectedSymptoms.length && 
                                        !geoApiParams.city && 
                                        !targetPostalCode;
      
      // Skip duplicate requests, but allow initial fetch
      if (!isInitialNationwideFetch && requestKey === lastRequestKeyRef.current) {
        return;
      }
      
      lastRequestKeyRef.current = requestKey;
      
      try {
        setLoading(true);
        setError(null);
        
        const params = new URLSearchParams();
        params.append('week', selectedWeek);
        
        if (selectedSymptoms.length > 0) {
          params.append('symptoms', selectedSymptoms.join(','));
        }
        
        if (geoApiParams.city) {
          params.append('city', geoApiParams.city);
        }
        
        if (targetPostalCode) {
          params.append('postalCode', targetPostalCode);
        }
        
        // ✅ DEBUG: Log fetch type
        console.log(`🔍 HDC: ${isInitialNationwideFetch ? 'INITIAL NATIONWIDE' : 'FILTERED'} fetch for week ${selectedWeek}`, {
          city: geoApiParams.city || 'none',
          postalCode: targetPostalCode || 'none',
          symptoms: selectedSymptoms.length ? selectedSymptoms.join(',') : 'none'
        });
        
        // ✅ FIXED: Use API_CONFIG.baseUrl for Railway backend
        const response = await fetch(`${API_CONFIG.baseUrl}/health-data/postal-groups?${params.toString()}`);
        const result = await response.json();
        
        if (result.success) {
          console.log(`✅ HDC: Successfully fetched ${result.data?.length || 0} postal groups for Dashboard`);
          setCurrentWeekData(result.data || []);
          
          // Mark initial fetch as done
          if (isInitialNationwideFetch) {
            initialFetchDoneRef.current = true;
          }
        } else {
          throw new Error(result.error || 'Failed to fetch data');
        }
      } catch (err) {
        console.error('❌ HDC fetch error:', err.message);
        setError(err.message);
        setCurrentWeekData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    }, [selectedWeek, selectedSymptoms.join(','), geoApiParams.city, selectedPostalCode]);
  
  // ===== BASIC HANDLERS - ENKLA =====
  const handleWeekChange = (week) => {
    setSelectedWeek(Number(week));
    setSelectedPeriod(`week_${week}_2024`);
    setError(null);
  };
  
  const handleSymptomChange = (symptoms) => {
    setSelectedSymptoms(symptoms);
    setError(null);
  };
  
  const updateGeoApiParams = (newGeoParams) => {
    setGeoApiParams(prev => ({
      ...prev,
      ...newGeoParams
    }));
  };
  
  const clearGeoApiParams = () => {
    setGeoApiParams({
      city: null,
      postalCode: null,
      country: null
    });
  };
  
  const handlePostalCodeClick = (postalCode) => {
    if (selectedPostalCode === postalCode) {
      setSelectedPostalCode(null);
      setGeoApiParams(prev => ({
        ...prev,
        postalCode: null
      }));
    } else {
      setSelectedPostalCode(postalCode);
      setGeoApiParams(prev => ({
        ...prev,
        postalCode: postalCode
      }));
    }
  };
  
  const resetToNationalView = () => {
    setSelectedPostalCode(null);
    clearGeoApiParams();
    // Reset initial fetch flag to allow nationwide refetch
    initialFetchDoneRef.current = false;
  };
  
  const clearSymptoms = () => {
    setSelectedSymptoms([]);
  };
  
  // ===== HISTORICAL DATA FUNCTIONS - PLACEHOLDER =====
  const fetchHistoricalData = async (postalCode, options = {}) => {
    // Placeholder - real implementation would be in a hook
    return { success: false, data: [], error: 'Not implemented in simple context' };
  };
  
  const getCachedHistoricalData = (postalCode, options = {}) => {
    return null; // Placeholder
  };
  
  const getHistoricalLoadingState = (postalCode, options = {}) => {
    const { weekCount = 5, includeCurrentWeek = true } = options;
    const cacheKey = `${postalCode}_${weekCount}_${includeCurrentWeek}`;
    return historicalLoading.get(cacheKey) || false;
  };
  
  const getHistoricalErrorState = (postalCode, options = {}) => {
    const { weekCount = 5, includeCurrentWeek = true } = options;
    const cacheKey = `${postalCode}_${weekCount}_${includeCurrentWeek}`;
    return historicalErrors.get(cacheKey) || null;
  };
  
  const clearHistoricalDataCache = () => {
    setHistoricalDataCache(new Map());
    setHistoricalLoading(new Map());
    setHistoricalErrors(new Map());
  };
  
  // ===== CONTEXT VALUE - ENKEL =====
  const contextValue = {
    // Basic state
    selectedWeek,
    setSelectedWeek,
    selectedPeriod,
    setSelectedPeriod,
    selectedPostalCode,
    setSelectedPostalCode,
    loading,
    setLoading,
    error,
    setError,
    
    // Symptom state
    selectedSymptoms,
    setSelectedSymptoms,
    availableSymptoms,
    hasSymptoms: selectedSymptoms.length > 0,
    symptomCount: selectedSymptoms.length,
    
    // Geographic state
    geoApiParams,
    setGeoApiParams,
    updateGeoApiParams,
    clearGeoApiParams,
    
    // Data state
    currentWeekData,
    setCurrentWeekData,
    
    // Map state
    mapCenter,
    setMapCenter,
    zoom,
    setZoom,
    visualizationType,
    setVisualizationType,
    
    // Basic handlers
    handleWeekChange,
    handleSymptomChange,
    handlePostalCodeClick,
    resetToNationalView,
    clearSymptoms,
    
    // Utility functions
    formatPostalCode,
    normalizePosition,
    
    // Historical data functions (placeholders)
    fetchHistoricalData,
    getCachedHistoricalData,
    getHistoricalLoadingState,
    getHistoricalErrorState,
    clearHistoricalDataCache,
    historicalDataCache,
    
    // Helper getters
    availableWeeks: [51, 52, 53],
    latestWeek: 53,
    earliestWeek: 51,
    
    // Data metadata
    dataStats: {
      totalRecords: currentWeekData.length,
      hasSymptomFilter: selectedSymptoms.length > 0,
      hasGeoFilter: !!(geoApiParams.city || geoApiParams.postalCode || geoApiParams.country),
      currentWeek: selectedWeek,
      currentSymptoms: selectedSymptoms,
      currentGeoFilters: geoApiParams
    }
  };
  
  return (
    <HealthDataContext.Provider value={contextValue}>
      {children}
    </HealthDataContext.Provider>
  );
};

// ===== CUSTOM HOOK =====
export const useHealthData = () => {
  const context = useContext(HealthDataContext);
  if (!context) {
    throw new Error('useHealthData must be used within a HealthDataProvider');
  }
  return context;
};

export default HealthDataContext;